// src/pages/RegistrationSuccess.tsx
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import Navbar from "../components/Navbar";
import { C, FONT } from "../theme";
import { generateDirectReceiptPDF } from "../utils/generateReceiptPDF";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AthleteEntry {
  athleteId?: string;
  slug?: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  club: string;
  division: string;
  weightCategory: string;
  category?: string;
  price: number;
}

interface SuccessState {
  referenceCode: string;
  competitionName: string;
  athletes: AthleteEntry[];
  totalAmount: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function RegistrationSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const state = location.state as SuccessState | null;
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // ── State guard ───────────────────────────────────────────────────────────
  // The /success route is dispatched in App.tsx:
  //   - With location.state  → this component (full receipt)
  //   - Without location.state → PaymentSuccess (BOG-redirect flow)
  // So if we get here with no state (e.g. a direct URL hit or a page refresh
  // after navigating away), the safest fallback is to send the coach to
  // their history where they can see and download the receipt.
  if (!state?.referenceCode) {
    navigate("/history", { replace: true });
    return null;
  }

  const { referenceCode, competitionName, athletes, totalAmount } = state;

  // ── PDF generation — uses the same shared utility as CoachHistory ────────

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    setPdfError(null);
    try {
      await generateDirectReceiptPDF({
        referenceCode,
        competitionName,
        createdAt: new Date(),
        totalAmount,
        paymentType: "website",
        coachName: currentUser?.displayName ?? undefined,
        coachEmail: currentUser?.email ?? undefined,
        athletes: athletes.map((a) => ({
          athleteId: a.athleteId,
          slug: a.slug,
          firstName: a.firstName,
          lastName: a.lastName,
          dateOfBirth: a.dateOfBirth,
          category: a.category,
          club: a.club,
          division: a.division,
          weightCategory: a.weightCategory,
          price: a.price,
        })),
      });
    } catch (err) {
      console.error("[PDF] Fatal error during PDF generation:", err);
      setPdfError(
        `PDF generation failed: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setPdfLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={s.page} className="page-content">
      <Navbar />
      <div style={s.container}>

        <div style={s.heroCard}>
          <div style={s.successCircle}>✓</div>
          <div style={s.pageLabel}>{t("success.pageLabel")}</div>
          <div style={s.heading}>{t("success.heading")}</div>
          <div style={s.subheading}>{t("success.subheading")}</div>
          <div style={s.refCodeBox}>
            <div style={s.refCodeLabel}>{t("success.refCode")}</div>
            <div style={s.refCode}>{referenceCode}</div>
          </div>
        </div>

        <div style={s.card}>
          <div style={s.detailRow}>
            <span style={s.detailLabel}>{t("success.competition")}</span>
            <span style={s.detailValue}>{competitionName}</span>
          </div>
          <div style={s.divider} />

          <div style={s.sectionLabel}>
            {t("success.athletes")} ({athletes.length})
          </div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>{t("success.table.athlete")}</th>
                <th style={s.th}>DOB</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>{t("success.table.club")}</th>
                <th style={s.th}>{t("success.table.division")}</th>
                <th style={s.th}>{t("success.table.weight")}</th>
                <th style={s.th}>{t("success.table.price")}</th>
              </tr>
            </thead>
            <tbody>
              {athletes.map((a, idx) => (
                <tr key={idx}>
                  <td style={{ ...s.td, fontWeight: 600, color: C.text }}>
                    {a.firstName} {a.lastName}
                  </td>
                  <td style={s.td}>{a.dateOfBirth ?? "—"}</td>
                  <td style={s.td}>{a.category ?? "—"}</td>
                  <td style={s.td}>{a.club}</td>
                  <td style={s.td}>{a.division}</td>
                  <td style={s.td}>{a.weightCategory}</td>
                  <td style={{ ...s.td, fontWeight: 600, color: C.text }}>
                    {a.price} GEL
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={s.divider} />

          <div style={s.totalRow}>
            <span style={s.totalLabel}>{t("success.total")}</span>
            <span style={s.totalAmount}>{totalAmount} GEL</span>
          </div>
        </div>

        {pdfError && (
          <div style={s.errorBanner}>⚠️ {pdfError}</div>
        )}

        <div style={s.actions}>
          <button
            style={{
              ...s.pdfBtn,
              opacity: pdfLoading ? 0.6 : 1,
              cursor: pdfLoading ? "not-allowed" : "pointer",
            }}
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
          >
            {pdfLoading ? "⏳ Generating PDF…" : `↓ ${t("success.downloadPdf")}`}
          </button>
          <button style={s.dashBtn} onClick={() => navigate("/dashboard")}>
            {t("success.backDashboard")}
          </button>
        </div>

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: {
    maxWidth: "720px", margin: "0 auto", padding: "40px 24px",
    display: "flex", flexDirection: "column", gap: "20px",
  },
  heroCard: {
    backgroundColor: C.surface, border: `1px solid ${C.border}`,
    borderRadius: "14px", padding: "44px 40px",
    display: "flex", flexDirection: "column", alignItems: "center",
    textAlign: "center", gap: "10px",
  },
  successCircle: {
    width: "68px", height: "68px", borderRadius: "50%",
    backgroundColor: C.success, color: "#fff",
    fontSize: "34px", fontWeight: 700,
    display: "flex", alignItems: "center", justifyContent: "center",
    marginBottom: "6px",
  },
  pageLabel: {
    fontSize: "10px", fontWeight: 700, letterSpacing: "2px",
    color: C.red, textTransform: "uppercase",
  },
  heading: {
    fontFamily: FONT.display, fontSize: "34px",
    color: C.text, letterSpacing: "1px", lineHeight: 1,
  },
  subheading: { fontSize: "14px", color: C.text2, marginBottom: "6px" },
  refCodeBox: {
    backgroundColor: C.surface2, border: `2px solid ${C.border}`,
    borderRadius: "10px", padding: "16px 36px", marginTop: "10px",
  },
  refCodeLabel: {
    fontSize: "10px", fontWeight: 700, letterSpacing: "1.5px",
    color: C.text3, textTransform: "uppercase", marginBottom: "6px",
  },
  refCode: {
    fontFamily: "monospace", fontSize: "28px",
    fontWeight: 700, color: C.red, letterSpacing: "4px",
  },
  card: {
    backgroundColor: C.surface, border: `1px solid ${C.border}`,
    borderRadius: "12px", padding: "26px",
  },
  detailRow: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", marginBottom: "4px",
  },
  detailLabel: {
    fontSize: "11px", fontWeight: 700, color: C.text3,
    textTransform: "uppercase", letterSpacing: "0.8px",
  },
  detailValue: { fontSize: "15px", fontWeight: 600, color: C.text },
  divider: { height: "1px", backgroundColor: C.border, margin: "16px 0" },
  sectionLabel: {
    fontSize: "11px", fontWeight: 700, color: C.text3,
    textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "12px",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left", padding: "9px 12px", backgroundColor: C.surface2,
    color: C.text2, fontWeight: 700, fontSize: "11px",
    textTransform: "uppercase", letterSpacing: "0.5px",
  },
  td: {
    padding: "11px 12px", borderBottom: `1px solid ${C.border}`,
    color: C.text2, fontSize: "13px",
  },
  totalRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  totalLabel: { fontSize: "14px", fontWeight: 600, color: C.text2 },
  totalAmount: { fontSize: "26px", fontWeight: 700, color: C.red },
  errorBanner: {
    backgroundColor: C.redDim,
    border: `1px solid rgba(218,41,28,0.3)`,
    borderRadius: "8px", padding: "12px 16px",
    fontSize: "13px", color: C.red,
  },
  actions: { display: "flex", gap: "12px", flexWrap: "wrap" },
  pdfBtn: {
    padding: "13px 24px", backgroundColor: C.surface, color: C.text,
    border: `1px solid ${C.border2}`, borderRadius: "8px",
    fontSize: "14px", fontWeight: 600,
  },
  dashBtn: {
    flex: 1, padding: "13px 24px", backgroundColor: C.red, color: "#fff",
    border: "none", borderRadius: "8px", fontSize: "14px",
    fontWeight: 700, cursor: "pointer", letterSpacing: "0.3px",
  },
};